import { useEffect, useState, useCallback, useRef } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useScenarioStore } from '../store/useScenarioStore';
import { useAppModeStore } from '../store/useAppModeStore';
import type { VehicleInput } from '../types';
import VehicleEditor from '../components/VehicleEditor';
import ImportExportBar from '../components/ImportExportBar';
import StateSelector from '../components/StateSelector';
import InputCompletenessIndicator from '../components/InputCompleteness';
import { getGeographicDefaults, getGasPrices } from '../utils/api';
import { useScenarioAutosave } from '../hooks/useScenarioAutosave';

const HORIZON_OPTIONS = [
  { months: 24, label: '24 months (2 years)' },
  { months: 36, label: '36 months (3 years)' },
  { months: 48, label: '48 months (4 years)' },
  { months: 60, label: '60 months (5 years)' },
];

export default function ScenarioEditorPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const {
    currentScenario,
    currentScenarioId,
    loading,
    error,
    loadScenario,
    resetToNew,
    setCurrentScenario,
    patchScenario,
    setHorizonMonths,
    addVehicle,
    updateVehicle,
    removeVehicle,
    duplicateVehicle,
    saveScenario,
    runCompute,
    clearError,
    importScenario,
    setAppMode,
  } = useScenarioStore();
  // Phase 24.2: gate fleet-only tax settings on the nav-level activeMode.
  const isFleet = useAppModeStore((s) => s.isFleet);

  const [editingVehicleId, setEditingVehicleId] = useState<string | null>(null);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved'>('idle');

  // Phase 22.1 B: debounced autosave. Individual mode requires a non-empty
  // scenario name AND ≥1 vehicle before the first POST is issued.
  const trimmedName = (currentScenario.name ?? '').trim();
  const readyToAutosave =
    trimmedName.length > 0 && currentScenario.vehicles.length > 0;
  const autosave = useScenarioAutosave({
    enabled: true,
    readyToSave: readyToAutosave,
  });
  const [analyzing, setAnalyzing] = useState(false);
  const [analyzeError, setAnalyzeError] = useState<string | null>(null);
  const [rawMiles, setRawMiles] = useState<string | null>(null);
  const [rawTaxRate, setRawTaxRate] = useState<string | null>(null);
  const [geoAutoFilled, setGeoAutoFilled] = useState(false);
  const [geoRegNote, setGeoRegNote] = useState<string | null>(null);
  const [geoGasPriceLabel, setGeoGasPriceLabel] = useState<string | null>(null);

  const currentScenarioRef = useRef(currentScenario);
  useEffect(() => {
    currentScenarioRef.current = currentScenario;
  }, [currentScenario]);

  const isNew = !id || id === 'new';

  useEffect(() => {
    if (isNew) {
      // Phase 22.4: lock the draft into individual mode when entering the
      // new-scenario route. If a draft is already in progress in fleet mode,
      // discard it and start fresh.
      const sameDraft =
        currentScenarioId === null && currentScenario.mode === 'individual';
      if (!sameDraft) {
        resetToNew('individual');
      } else {
        setAppMode('individual');
      }
      // Phase 22.4: optional seed from "Create new in Individual mode"
      // affordance on the fleet editor. Copies the first vehicle of the
      // fleet draft; quantities and other line items are dropped.
      const seed = (location.state as { seedFromFleet?: { scenarioName: string; vehicle: VehicleInput | null } } | null)
        ?.seedFromFleet;
      if (seed && seed.vehicle) {
        setTimeout(() => {
          const store = useScenarioStore.getState();
          if (store.currentScenarioId !== null) return;
          store.patchScenario({ name: (seed.scenarioName || 'New Scenario').trim() });
          // Replace any seeded vehicle list with a clean copy.
          const copied: VehicleInput = {
            ...seed.vehicle!,
            vehicle_id: crypto.randomUUID(),
          };
          store.setCurrentScenario({
            ...store.currentScenario,
            vehicles: [copied],
          });
          navigate(location.pathname, { replace: true });
        }, 0);
      }
    } else {
      // Match string and numeric IDs (Supabase = uuid, SQLite = int).
      const idMatch =
        currentScenarioId !== null &&
        String(currentScenarioId) === String(id);
      if (!idMatch) {
        loadScenario(id as string);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Phase 22.4: cross-mode URL redirect. If the scenario we just loaded is
  // a fleet scenario but we're under /scenario/individual/:id, bounce.
  useEffect(() => {
    if (
      !isNew &&
      currentScenarioId !== null &&
      String(currentScenarioId) === String(id) &&
      currentScenario.mode === 'fleet'
    ) {
      navigate(`/scenario/fleet/${currentScenarioId}`, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentScenarioId, currentScenario.mode]);

  async function handleSave() {
    setSaveStatus('saving');
    try {
      const savedId = await saveScenario();
      setSaveStatus('saved');
      if (isNew && savedId) {
        navigate(`/scenario/individual/${savedId}`, { replace: true });
      }
      setTimeout(() => setSaveStatus('idle'), 2000);
    } catch {
      setSaveStatus('idle');
    }
  }

  async function handleRunAnalysis() {
    if (currentScenario.vehicles.length === 0) {
      alert('Add at least one vehicle before running analysis.');
      return;
    }
    setAnalyzing(true);
    setAnalyzeError(null);
    try {
      await saveScenario();
      await runCompute();
      navigate('/compare');
    } catch (e) {
      setAnalyzeError((e as Error).message ?? 'Analysis failed. Please try again.');
    } finally {
      setAnalyzing(false);
    }
  }

  const applyGeographicDefaults = useCallback(async (stateCode: string) => {
    if (!stateCode) return;
    try {
      // Bug 1 fix: read latest Zustand state directly, not stale ref (ref lags behind updateVehicle calls)
      const scenario = useScenarioStore.getState().currentScenario;

      const [gasPrices] = await Promise.all([
        getGasPrices().catch(() => null),
      ]);

      const updatedVehicles = await Promise.all(
        scenario.vehicles.map(async (v) => {
          const msrp = v.depreciation.purchase_price || 30000;
          const currentYear = new Date().getFullYear();
          const vehicleAge = v.model_year ? Math.max(0, currentYear - v.model_year) : 0;
          const isEv = v.operating_costs.fuel_type === 'electric';

          const geo = await getGeographicDefaults(stateCode, msrp, vehicleAge, isEv, false).catch(() => null);
          if (!geo) return v;

          const patchOc: Partial<typeof v.operating_costs> = {
            annual_registration_fee: geo.registration.base_fee,
            electricity_cost_per_kwh: geo.electricity_rate_cents_kwh / 100,
          };
          if (isEv) {
            patchOc.ev_registration_surcharge = geo.registration.ev_surcharge;
          }
          if (gasPrices && !isEv) {
            patchOc.fuel_cost_per_gallon = v.operating_costs.requires_premium
              ? gasPrices.premium
              : gasPrices.regular;
          }
          return { ...v, operating_costs: { ...v.operating_costs, ...patchOc } };
        }),
      );

      const firstVehicle = scenario.vehicles[0];
      const scenarioMsrp = firstVehicle?.depreciation.purchase_price || 30000;
      const geo = await getGeographicDefaults(stateCode, scenarioMsrp).catch(() => null);

      const scenarioUpdates: Partial<typeof scenario> = {
        state_code: stateCode,
        vehicles: updatedVehicles,
      };
      if (geo) {
        scenarioUpdates.sales_tax_rate = geo.sales_tax_rate;
        setGeoRegNote(geo.registration.note);
      }

      setCurrentScenario({ ...scenario, ...scenarioUpdates });
      setGeoAutoFilled(true);

      if (gasPrices) {
        setGeoGasPriceLabel(
          gasPrices.is_fallback
            ? 'fallback'
            : 'National 52-week EIA average'
        );
      }
    } catch {
      // Silently fail — user can still enter values manually
    }
  }, [setCurrentScenario]);

  function handleStateChange(stateCode: string) {
    setGeoAutoFilled(false);
    setGeoRegNote(null);
    setGeoGasPriceLabel(null);
    if (stateCode) {
      applyGeographicDefaults(stateCode);
    }
  }

  const editingVehicle = editingVehicleId
    ? currentScenario.vehicles.find((v) => v.vehicle_id === editingVehicleId) ?? null
    : null;

  const horizonMonths = currentScenario.horizon_months ?? 60;
  const anyVehicleHasWinterTires = currentScenario.vehicles.some((v) => v.operating_costs.has_winter_tires);

  // Phase 22.7: which fuel-price fields are relevant for this scenario.
  // Empty fleet → show all three so the user can prefill before adding a vehicle.
  const fuelTypesPresent = new Set(
    currentScenario.vehicles.map((v) => v.operating_costs.fuel_type),
  );
  const showsGasolinePrice =
    currentScenario.vehicles.length === 0 ||
    fuelTypesPresent.has('gasoline') ||
    fuelTypesPresent.has('phev');
  const showsDieselPrice =
    currentScenario.vehicles.length === 0 || fuelTypesPresent.has('diesel');
  const showsElectricityPrice =
    currentScenario.vehicles.length === 0 ||
    fuelTypesPresent.has('electric') ||
    fuelTypesPresent.has('phev');
  const showsAnyFuelPrice =
    showsGasolinePrice || showsDieselPrice || showsElectricityPrice;
  const hasAnyScenarioFuelPrice =
    currentScenario.scenario_fuel_cost_per_gallon != null ||
    currentScenario.scenario_diesel_price_per_gallon != null ||
    currentScenario.scenario_electricity_rate_per_kwh != null;
  const resetScenarioFuelPrices = () =>
    patchScenario({
      scenario_fuel_cost_per_gallon: null,
      scenario_diesel_price_per_gallon: null,
      scenario_electricity_rate_per_kwh: null,
    });

  // Phase 23: tax settings panel state. The "What is this?" disclosure starts collapsed.
  const [taxHelpExpanded, setTaxHelpExpanded] = useState(false);
  const marginalTaxRatePct = Math.round(((currentScenario.marginal_tax_rate ?? 0.21) * 1000)) / 10;
  const businessUsePct = Math.round((currentScenario.business_use_pct ?? 1.0) * 100);
  const taxYear = currentScenario.tax_year ?? 2026;
  const sec179Limit = currentScenario.sec179_limit ?? 1_160_000;

  // Phase 26.5 — scenario-level cost-model controls.
  const [costModelOpen, setCostModelOpen] = useState(false);
  const applyMaintenanceAging = currentScenario.apply_maintenance_aging ?? true;
  const fuelInflationPct = currentScenario.fuel_inflation_rate_pct ?? null;
  const insuranceStepupPct = currentScenario.insurance_stepup_rate_pct ?? null;

  return (
    <div className="max-w-5xl mx-auto px-6 py-10">
      {/* Mode badge */}
      <div className="mb-6 flex items-center gap-2">
        <span className="inline-flex items-center px-2.5 py-1 rounded text-[11px] font-semibold uppercase tracking-widest"
          style={{
            background: 'var(--panel2)',
            border: '1px solid var(--border2)',
            color: 'var(--white-dim)',
            fontFamily: 'Barlow Condensed, sans-serif',
          }}
        >
          Individual mode
        </span>
        {!isNew && (
          <button
            onClick={() => {
              if (
                window.confirm(
                  'Create a new Fleet scenario? The first vehicle from this scenario will be copied as a fleet line item; per-vehicle quantities reset to 1.',
                )
              ) {
                navigate('/scenario/fleet/new', {
                  state: {
                    seedFromIndividual: {
                      scenarioName: currentScenario.name,
                      vehicle: currentScenario.vehicles[0] ?? null,
                      ownership_period_years:
                        currentScenario.vehicles[0]?.ownership_period_years ?? 5,
                    },
                  },
                });
              }
            }}
            className="text-xs underline underline-offset-2"
            style={{ color: 'var(--white-dim)', fontFamily: 'Barlow, sans-serif' }}
          >
            Create new in Fleet mode
          </button>
        )}
      </div>

      {/* Import / Export toolbar */}
      <div className="mb-6">
        <ImportExportBar
          currentScenario={currentScenario}
          onImport={async (json) => {
            const newId = await importScenario(json);
            // Phase 22.4: respect the imported scenario's mode when redirecting.
            const importedMode = json.mode === 'fleet' ? 'fleet' : 'individual';
            navigate(`/scenario/${importedMode}/${newId}`);
          }}
        />
      </div>

      {/* Breadcrumb */}
      <button
        onClick={() => navigate('/')}
        className="flex items-center gap-1.5 text-slate-400 hover:text-slate-200 text-sm mb-6 transition-colors"
      >
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
        </svg>
        All Scenarios
      </button>

      {/* Error */}
      {(error || analyzeError) && (
        <div className="mb-6 rounded-lg bg-red-900/30 border border-red-700 px-4 py-3 text-red-300 text-sm flex items-center justify-between">
          <span>{analyzeError ?? error}</span>
          <button
            onClick={() => { clearError(); setAnalyzeError(null); }}
            className="text-red-400 hover:text-red-200 ml-4"
          >
            ✕
          </button>
        </div>
      )}

      {/* Scenario Settings */}
      <div data-tour="assumptions-tab" className="cnc-panel rounded-xl p-6 mb-6">
        <h2 className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-4">Scenario Settings</h2>
        <div className="grid grid-cols-1 gap-4">
          <div className="flex flex-col gap-1">
            <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">
              Scenario Name
            </label>
            <input
              type="text"
              className="bg-slate-800 border border-slate-700 text-slate-100 font-semibold text-xl rounded-lg px-4 py-2.5 focus:outline-none focus:border-sky-500 focus:ring-1 focus:ring-sky-500 w-full"
              value={currentScenario.name}
              onChange={(e) => patchScenario({ name: e.target.value })}
              placeholder="e.g. Family SUV Comparison 2025"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">
              Description
              <span className="ml-1 normal-case font-normal text-slate-600">(optional)</span>
            </label>
            <textarea
              rows={2}
              className="bg-slate-800 border border-slate-700 text-slate-100 text-sm rounded-lg px-4 py-2.5 focus:outline-none focus:border-sky-500 focus:ring-1 focus:ring-sky-500 w-full placeholder:text-slate-500 resize-none"
              value={currentScenario.description ?? ''}
              onChange={(e) =>
                patchScenario({
                  description: e.target.value || null,
                })
              }
              placeholder="Add context about this comparison…"
            />
          </div>
          {/* State Selector */}
          <div className="flex flex-col gap-1">
            <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">
              State <span className="normal-case font-normal">(auto-fills tax, registration, electricity, gas price)</span>
            </label>
            <StateSelector
              value={currentScenario.state_code ?? ''}
              onChange={handleStateChange}
            />
            {geoAutoFilled && (
              <div className="mt-1 space-y-0.5">
                <p className="text-xs text-sky-400">
                  Sales tax, registration, electricity rate, and gas price auto-filled from {currentScenario.state_code} defaults.
                  {geoGasPriceLabel && geoGasPriceLabel !== 'fallback' && (
                    <span className="text-sky-400/70"> Gas price: {geoGasPriceLabel}</span>
                  )}
                  {geoGasPriceLabel === 'fallback' && (
                    <span className="text-amber-400"> Gas price: estimated — server starting up, refresh to update</span>
                  )}
                </p>
                {geoRegNote && (
                  <p className="text-xs text-slate-500">{geoRegNote}</p>
                )}
              </div>
            )}
            <p className="text-xs text-slate-600 mt-0.5">
              Sales tax and registration estimates are based on state-level averages and may not reflect your exact county or local rates. Verify with your state DMV for precise amounts.
            </p>
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">
              Ownership Horizon <span className="normal-case font-normal">(applies to all vehicles)</span>
            </label>
            <select
              className="bg-slate-800 border border-slate-700 text-slate-100 text-sm rounded-lg px-4 py-2.5 focus:outline-none focus:border-sky-500 w-full"
              value={horizonMonths}
              onChange={(e) => setHorizonMonths(Number(e.target.value))}
            >
              {HORIZON_OPTIONS.map((opt) => (
                <option key={opt.months} value={opt.months}>
                  {opt.label}
                </option>
              ))}
            </select>
            <p className="text-xs text-slate-500 mt-1">
              This horizon applies to all vehicles in this scenario for a fair comparison.
            </p>
          </div>

          {/* Annual Miles */}
          <div className="flex flex-col gap-1">
            <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">
              Annual Miles <span className="normal-case font-normal">(applies to all vehicles)</span>
            </label>
            <input
              type="text"
              inputMode="numeric"
              className="bg-slate-800 border border-slate-700 text-slate-100 text-sm rounded-lg px-4 py-2.5 focus:outline-none focus:border-sky-500 focus:ring-1 focus:ring-sky-500 w-full"
              value={rawMiles !== null ? rawMiles : (currentScenario.annual_miles ?? 12000).toLocaleString('en-US')}
              onFocus={() => setRawMiles(String(currentScenario.annual_miles ?? 12000))}
              onChange={(e) => setRawMiles(e.target.value)}
              onBlur={() => {
                const str = (rawMiles ?? '').replace(/,/g, '').trim();
                const parsed = parseInt(str, 10);
                patchScenario({
                  annual_miles: isNaN(parsed) ? 0 : Math.max(0, parsed),
                });
                setRawMiles(null);
              }}
            />
            <p className="text-xs text-slate-500 mt-1">Override per vehicle in Operating tab if needed.</p>
          </div>

          {/* Phase 22.7: Scenario-level Fuel Prices */}
          {showsAnyFuelPrice && (
            <div
              className="flex flex-col gap-3 rounded-xl"
              style={{
                background: 'var(--tco-surface)',
                border: '1px solid var(--tco-line)',
                padding: '16px 18px',
              }}
            >
              <div className="flex items-center justify-between gap-3">
                <p
                  className="text-[11px] font-semibold uppercase"
                  style={{
                    letterSpacing: '0.10em',
                    color: 'var(--tco-ink-3)',
                  }}
                >
                  Fuel Prices
                </p>
                {hasAnyScenarioFuelPrice && (
                  <button
                    type="button"
                    onClick={resetScenarioFuelPrices}
                    className="text-xs underline underline-offset-2"
                    style={{ color: 'var(--tco-ink-3)' }}
                  >
                    Reset to live prices
                  </button>
                )}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {showsGasolinePrice && (
                  <div className="flex flex-col gap-1">
                    <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">
                      Gasoline ($/gal)
                    </label>
                    <input
                      type="number"
                      step={0.01}
                      min={0}
                      placeholder="EIA default (~$3.45)"
                      className="bg-slate-800 border border-slate-700 text-slate-100 text-sm rounded-lg px-4 py-2.5 focus:outline-none focus:border-sky-500 focus:ring-1 focus:ring-sky-500 w-full"
                      value={
                        currentScenario.scenario_fuel_cost_per_gallon == null
                          ? ''
                          : currentScenario.scenario_fuel_cost_per_gallon
                      }
                      onChange={(e) => {
                        const raw = e.target.value;
                        if (raw === '') {
                          patchScenario({ scenario_fuel_cost_per_gallon: null });
                        } else {
                          const parsed = parseFloat(raw);
                          patchScenario({
                            scenario_fuel_cost_per_gallon: isNaN(parsed)
                              ? null
                              : Math.max(0, parsed),
                          });
                        }
                      }}
                    />
                    <p className="text-xs text-slate-500 mt-0.5">
                      Applies to all gasoline (and PHEV) vehicles. Override per-vehicle in the vehicle editor.
                    </p>
                  </div>
                )}

                {showsDieselPrice && (
                  <div className="flex flex-col gap-1">
                    <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">
                      Diesel ($/gal)
                    </label>
                    <input
                      type="number"
                      step={0.01}
                      min={0}
                      placeholder="EIA default (~$4.00)"
                      className="bg-slate-800 border border-slate-700 text-slate-100 text-sm rounded-lg px-4 py-2.5 focus:outline-none focus:border-sky-500 focus:ring-1 focus:ring-sky-500 w-full"
                      value={
                        currentScenario.scenario_diesel_price_per_gallon == null
                          ? ''
                          : currentScenario.scenario_diesel_price_per_gallon
                      }
                      onChange={(e) => {
                        const raw = e.target.value;
                        if (raw === '') {
                          patchScenario({ scenario_diesel_price_per_gallon: null });
                        } else {
                          const parsed = parseFloat(raw);
                          patchScenario({
                            scenario_diesel_price_per_gallon: isNaN(parsed)
                              ? null
                              : Math.max(0, parsed),
                          });
                        }
                      }}
                    />
                    <p className="text-xs text-slate-500 mt-0.5">
                      Applies to all diesel vehicles. Override per-vehicle in the vehicle editor.
                    </p>
                  </div>
                )}

                {showsElectricityPrice && (
                  <div className="flex flex-col gap-1">
                    <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">
                      Electricity ($/kWh)
                    </label>
                    <input
                      type="number"
                      step={0.01}
                      min={0}
                      placeholder="EIA default (~$0.16)"
                      className="bg-slate-800 border border-slate-700 text-slate-100 text-sm rounded-lg px-4 py-2.5 focus:outline-none focus:border-sky-500 focus:ring-1 focus:ring-sky-500 w-full"
                      value={
                        currentScenario.scenario_electricity_rate_per_kwh == null
                          ? ''
                          : currentScenario.scenario_electricity_rate_per_kwh
                      }
                      onChange={(e) => {
                        const raw = e.target.value;
                        if (raw === '') {
                          patchScenario({ scenario_electricity_rate_per_kwh: null });
                        } else {
                          const parsed = parseFloat(raw);
                          patchScenario({
                            scenario_electricity_rate_per_kwh: isNaN(parsed)
                              ? null
                              : Math.max(0, parsed),
                          });
                        }
                      }}
                    />
                    <p className="text-xs text-slate-500 mt-0.5">
                      Applies to all EV/PHEV vehicles. Override per-vehicle in the vehicle editor.
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Phase 23: Tax Settings (Section 179 + bonus depreciation).
              Phase 24.2: gated on nav-level isFleet — Section 179 + bonus
              depreciation only apply to commercial/fleet purchases, so the
              section is hidden when the user is in Personal mode. */}
          {isFleet && (
          <div
            data-testid="tax-settings-section"
            className="flex flex-col gap-3 rounded-xl"
            style={{
              background: 'var(--tco-surface)',
              border: '1px solid var(--tco-line)',
              padding: '16px 18px',
            }}
          >
            <p
              className="text-[11px] font-semibold uppercase"
              style={{
                letterSpacing: '0.10em',
                color: 'var(--tco-ink-3)',
              }}
            >
              Tax Settings
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Marginal tax rate */}
              <div className="flex flex-col gap-1">
                <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">
                  Marginal Tax Rate
                </label>
                <div className="relative">
                  <input
                    type="number"
                    step={0.5}
                    min={0}
                    max={99}
                    className="bg-slate-800 border border-slate-700 text-slate-100 text-sm rounded-lg px-4 py-2.5 focus:outline-none focus:border-sky-500 focus:ring-1 focus:ring-sky-500 w-full pr-10"
                    value={marginalTaxRatePct}
                    onChange={(e) => {
                      const pct = Math.min(99, Math.max(0, Number(e.target.value)));
                      patchScenario({ marginal_tax_rate: pct / 100 });
                    }}
                  />
                  <span className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 text-sm pointer-events-none">%</span>
                </div>
                <p className="text-xs text-slate-500 mt-0.5">
                  Federal income tax rate. Common: 21% (C-corp), 22–37% (individual).
                </p>
              </div>

              {/* Business use */}
              <div className="flex flex-col gap-1">
                <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">
                  Business Use
                </label>
                <div className="relative">
                  <input
                    type="number"
                    step={1}
                    min={0}
                    max={100}
                    className="bg-slate-800 border border-slate-700 text-slate-100 text-sm rounded-lg px-4 py-2.5 focus:outline-none focus:border-sky-500 focus:ring-1 focus:ring-sky-500 w-full pr-10"
                    value={businessUsePct}
                    onChange={(e) => {
                      const pct = Math.min(100, Math.max(0, Number(e.target.value)));
                      patchScenario({ business_use_pct: pct / 100 });
                    }}
                  />
                  <span className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 text-sm pointer-events-none">%</span>
                </div>
                <p className="text-xs text-slate-500 mt-0.5">
                  Vehicles used less than 100% for business have proportionally reduced deductions.
                </p>
              </div>

              {/* Tax year */}
              <div className="flex flex-col gap-1">
                <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">
                  Tax Year
                </label>
                <select
                  className="bg-slate-800 border border-slate-700 text-slate-100 text-sm rounded-lg px-4 py-2.5 focus:outline-none focus:border-sky-500 w-full"
                  value={taxYear}
                  onChange={(e) => patchScenario({ tax_year: Number(e.target.value) })}
                >
                  <option value={2024}>2024 (60% bonus)</option>
                  <option value={2025}>2025 (40% bonus)</option>
                  <option value={2026}>2026 (20% bonus)</option>
                  <option value={2027}>2027 (0% bonus)</option>
                </select>
                <p className="text-xs text-slate-500 mt-0.5">
                  Determines bonus depreciation percentage (60%/40%/20%/0%).
                </p>
              </div>

              {/* Section 179 limit */}
              <div className="flex flex-col gap-1">
                <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">
                  Section 179 Limit <span className="normal-case font-normal text-slate-600">(Advanced)</span>
                </label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 text-sm pointer-events-none">$</span>
                  <input
                    type="number"
                    step={10000}
                    min={0}
                    className="bg-slate-800 border border-slate-700 text-slate-100 text-sm rounded-lg pl-7 pr-4 py-2.5 focus:outline-none focus:border-sky-500 focus:ring-1 focus:ring-sky-500 w-full"
                    value={sec179Limit}
                    onChange={(e) => {
                      const v = Math.max(0, Number(e.target.value));
                      patchScenario({ sec179_limit: v });
                    }}
                  />
                </div>
                <p className="text-xs text-slate-500 mt-0.5">
                  Annual IRS limit. Default is the 2023 indexed limit ($1,160,000).
                </p>
              </div>
            </div>

            {/* Collapsible "What is this?" */}
            <div className="pt-1">
              <button
                type="button"
                onClick={() => setTaxHelpExpanded((b) => !b)}
                className="text-xs underline underline-offset-2"
                style={{ color: 'var(--tco-ink-3)' }}
              >
                {taxHelpExpanded ? '− What is this?' : '+ What is this?'}
              </button>
              {taxHelpExpanded && (
                <p className="text-xs mt-2" style={{ color: 'var(--tco-ink-2)' }}>
                  Section 179 and bonus depreciation are IRS provisions that let businesses deduct the cost of commercial vehicles in the year of purchase, rather than depreciating over 5–7 years. This creates a significant first-year cash benefit that reduces your effective vehicle cost.
                </p>
              )}
            </div>
          </div>
          )}

          {/* Phase 26.5 — Cost Model (Advanced). Maintenance aging + fuel
              and insurance inflation. Applies to every vehicle in the
              scenario. Personal mode defaults: gasoline fuel + personal
              insurance step-up. Empty inputs use the research-derived
              defaults; explicit 0 disables the escalator. */}
          <div
            data-testid="cost-model-advanced-section"
            data-tour="cost-model-advanced"
            className="flex flex-col gap-3 rounded-xl"
            style={{
              background: 'var(--tco-surface)',
              border: '1px solid var(--tco-line)',
              padding: '16px 18px',
            }}
          >
            <button
              type="button"
              onClick={() => setCostModelOpen((b) => !b)}
              className="w-full flex items-center justify-between gap-3"
              style={{ background: 'transparent', border: 'none', padding: 0, cursor: 'pointer' }}
              aria-expanded={costModelOpen}
              aria-controls="personal-cost-model-body"
            >
              <span
                className="text-[11px] font-semibold uppercase"
                style={{ letterSpacing: '0.10em', color: 'var(--tco-ink-3)' }}
              >
                {costModelOpen ? '−' : '+'} Cost Model (Advanced)
                <span className="ml-2 normal-case font-normal" style={{ color: 'var(--tco-ink-3)' }}>
                  maintenance aging, fuel & insurance escalation
                </span>
              </span>
              <span className="text-xs" style={{ color: 'var(--tco-ink-3)' }}>
                {applyMaintenanceAging ? 'Aging on' : 'Aging off'} ·{' '}
                Fuel {fuelInflationPct == null ? 'default' : `${fuelInflationPct}%/yr`} ·{' '}
                Ins {insuranceStepupPct == null ? 'default' : `${insuranceStepupPct}%/yr`}
              </span>
            </button>

            {costModelOpen && (
              <div
                id="personal-cost-model-body"
                className="grid grid-cols-1 sm:grid-cols-2 gap-4"
              >
                <label className="flex items-start gap-2 col-span-full">
                  <input
                    type="checkbox"
                    checked={applyMaintenanceAging}
                    onChange={(e) =>
                      patchScenario({ apply_maintenance_aging: e.target.checked })
                    }
                    className="mt-1"
                  />
                  <span className="text-sm" style={{ color: 'var(--tco-ink-2)' }}>
                    Apply maintenance aging curve
                    <p className="text-xs" style={{ color: 'var(--tco-ink-3)' }}>
                      Year 1 = base; year 3-5 scale up using NPTC / AAA / Automotive Fleet benchmarks.
                      Uncheck to keep maintenance flat across all years.
                    </p>
                  </span>
                </label>

                <div className="flex flex-col gap-1">
                  <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">
                    Fuel Inflation
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      step={0.1}
                      min={-10}
                      max={20}
                      placeholder="2.3 (gasoline default)"
                      className="bg-slate-800 border border-slate-700 text-slate-100 text-sm rounded-lg px-4 py-2.5 focus:outline-none focus:border-sky-500 focus:ring-1 focus:ring-sky-500 w-full pr-10"
                      value={fuelInflationPct ?? ''}
                      onChange={(e) => {
                        const txt = e.target.value;
                        patchScenario({
                          fuel_inflation_rate_pct: txt === '' ? null : Number(txt),
                        });
                      }}
                    />
                    <span className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 text-sm pointer-events-none">
                      %/yr
                    </span>
                  </div>
                  <p className="text-xs text-slate-500 mt-0.5">
                    Blank = EIA-derived default per fuel type. 0 = disabled.
                  </p>
                </div>

                <div className="flex flex-col gap-1">
                  <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">
                    Insurance Step-Up
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      step={0.1}
                      min={-10}
                      max={20}
                      placeholder="5.0 (personal default)"
                      className="bg-slate-800 border border-slate-700 text-slate-100 text-sm rounded-lg px-4 py-2.5 focus:outline-none focus:border-sky-500 focus:ring-1 focus:ring-sky-500 w-full pr-10"
                      value={insuranceStepupPct ?? ''}
                      onChange={(e) => {
                        const txt = e.target.value;
                        patchScenario({
                          insurance_stepup_rate_pct: txt === '' ? null : Number(txt),
                        });
                      }}
                    />
                    <span className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 text-sm pointer-events-none">
                      %/yr
                    </span>
                  </div>
                  <p className="text-xs text-slate-500 mt-0.5">
                    Blank = BLS auto-insurance CPI default. 0 = disabled.
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Sales Tax Rate */}
          <div className="flex flex-col gap-1">
            <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">
              Sales Tax Rate <span className="normal-case font-normal">(applies to all vehicles)</span>
            </label>
            <div className="relative">
              <input
                type="text"
                inputMode="decimal"
                className="bg-slate-800 border border-slate-700 text-slate-100 text-sm rounded-lg px-4 py-2.5 focus:outline-none focus:border-sky-500 focus:ring-1 focus:ring-sky-500 w-full pr-10"
                value={rawTaxRate !== null ? rawTaxRate : String(Math.round((currentScenario.sales_tax_rate ?? 0.08) * 1000) / 10)}
                onFocus={() => setRawTaxRate(String(Math.round((currentScenario.sales_tax_rate ?? 0.08) * 1000) / 10))}
                onChange={(e) => setRawTaxRate(e.target.value)}
                onBlur={() => {
                  const parsed = parseFloat((rawTaxRate ?? '').trim());
                  patchScenario({
                    sales_tax_rate: Math.min(0.20, Math.max(0, (isNaN(parsed) ? 0 : parsed) / 100)),
                  });
                  setRawTaxRate(null);
                }}
              />
              <span className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 text-sm pointer-events-none">%</span>
            </div>
            <p className="text-xs text-slate-500 mt-1">
              Applied to the purchase price of all vehicles in this scenario.
              {geoAutoFilled && currentScenario.state_code && (
                <span className="text-sky-400/70"> (auto-filled from {currentScenario.state_code} defaults)</span>
              )}
            </p>
          </div>

          {/* Expected Investment Return Rate */}
          <div className="flex flex-col gap-1">
            <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">
              Expected Investment Return Rate <span className="normal-case font-normal">(applies to all vehicles)</span>
            </label>
            <div className="relative">
              <input
                type="number"
                className="bg-slate-800 border border-slate-700 text-slate-100 text-sm rounded-lg px-4 py-2.5 focus:outline-none focus:border-sky-500 focus:ring-1 focus:ring-sky-500 w-full pr-10"
                value={Math.round((currentScenario.investment_return_rate ?? 0.07) * 1000) / 10}
                step={0.1}
                min={1}
                max={20}
                onChange={(e) => patchScenario({
                  investment_return_rate: Math.min(0.20, Math.max(0.01, Number(e.target.value) / 100)),
                })}
              />
              <span className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 text-sm pointer-events-none">%</span>
            </div>
            <p className="text-xs text-slate-500 mt-1">
              Annual return on capital not invested in this vehicle. Used to estimate opportunity cost. Common values: 5-7% for index funds, 3-4% for bonds.
            </p>
          </div>

          {/* Winter Driving % — only shown when at least one vehicle has winter tires */}
          {anyVehicleHasWinterTires && (
            <div className="flex flex-col gap-1">
              <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">
                Winter Driving Percentage
              </label>
              <div className="relative">
                <input
                  type="number"
                  className="bg-slate-800 border border-slate-700 text-slate-100 text-sm rounded-lg px-4 py-2.5 focus:outline-none focus:border-sky-500 focus:ring-1 focus:ring-sky-500 w-full pr-10"
                  value={Math.round((currentScenario.winter_miles_pct ?? 0.40) * 100)}
                  step={5}
                  min={1}
                  max={99}
                  onChange={(e) => patchScenario({
                    winter_miles_pct: Math.min(0.99, Math.max(0.01, Number(e.target.value) / 100)),
                  })}
                />
                <span className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 text-sm pointer-events-none">%</span>
              </div>
              <p className="text-xs text-slate-500 mt-1">Fraction of annual miles driven on winter tires. Applies to all vehicles with winter tires enabled.</p>
            </div>
          )}

          {/* Financial vs Emotional Balance */}
          <div className="flex flex-col gap-2">
            <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">
              Decision Weight Balance
            </label>
            {(() => {
              const fw = Math.round((currentScenario.financial_weight ?? 0.70) * 100);
              const ew = 100 - fw;
              return (
                <>
                  <div className="flex justify-between text-xs text-slate-300 mb-1">
                    <span>Financial</span>
                    <span className="font-semibold" style={{ color: 'var(--gold)' }}>Financial {fw}% / Emotional {ew}%</span>
                    <span>Emotional</span>
                  </div>
                  <input
                    type="range"
                    min={0}
                    max={100}
                    step={5}
                    value={fw}
                    onChange={(e) => {
                      const f = Number(e.target.value) / 100;
                      patchScenario({ financial_weight: f, emotional_weight: 1 - f });
                    }}
                    className="w-full h-1.5 rounded cursor-pointer" style={{ accentColor: 'var(--red-bright)' }}
                  />
                  <div className="flex justify-between text-xs text-slate-600">
                    <span>0% Financial</span>
                    <span>100% Financial</span>
                  </div>
                </>
              );
            })()}
          </div>

        </div>
      </div>

      {/* Vehicles section */}
      <div data-tour="vehicle-selector" className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold" style={{ color: 'var(--tco-ink)' }}>
            Vehicles
            <span className="ml-2 text-sm font-normal" style={{ color: 'var(--tco-ink-3)' }}>
              {currentScenario.vehicles.length} added
            </span>
          </h2>
          <button
            onClick={() => { const newId = addVehicle(); setEditingVehicleId(newId); }}
            className="btn-secondary inline-flex items-center gap-2 text-sm px-4 py-2 rounded-lg"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
            </svg>
            Add Vehicle
          </button>
        </div>

        {currentScenario.vehicles.length === 0 && (
          <div className="text-center py-16 rounded-xl text-sm" style={{ border: '1px dashed var(--border2)', color: 'var(--white-dim)' }}>
            No vehicles added yet. Click <strong style={{ color: 'var(--white)' }}>Add Vehicle</strong> to get started.
          </div>
        )}

        {currentScenario.vehicles.length > 0 && (
          <div className="space-y-3">
            {currentScenario.vehicles.map((v, idx) => (
              <div
                key={v.vehicle_id}
                className="cnc-panel flex items-center justify-between rounded-xl px-5 py-4 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <span className="text-xs font-mono w-5 text-center" style={{ color: 'var(--tco-ink-3)' }}>{idx + 1}</span>
                  <div>
                    <p className="font-medium" style={{ color: 'var(--tco-ink)' }}>{v.label}</p>
                    <p className="text-xs mt-0.5" style={{ color: 'var(--tco-ink-2)' }}>
                      ${v.depreciation.purchase_price.toLocaleString()} &middot;{' '}
                      {v.operating_costs.fuel_type} &middot;{' '}
                      {horizonMonths}-mo horizon
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setEditingVehicleId(v.vehicle_id)}
                    className="btn-secondary px-3 py-1.5 text-xs rounded-lg"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => duplicateVehicle(v.vehicle_id)}
                    title="Duplicate vehicle"
                    className="px-2 py-1.5 text-slate-400 hover:text-sky-400 hover:bg-sky-900/20 text-xs font-medium rounded-lg transition-colors"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                  </button>
                  <button
                    onClick={() => removeVehicle(v.vehicle_id)}
                    className="px-3 py-1.5 text-slate-400 hover:text-red-400 hover:bg-red-900/20 text-xs font-medium rounded-lg transition-colors"
                  >
                    Remove
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Phase 25: input completeness sits above the action bar so the user
          can review defaults before hitting Run Analysis. */}
      <div data-tour="input-completeness">
        <InputCompletenessIndicator scenario={currentScenario} />
      </div>

      {/* Action bar */}
      <div className="flex items-center justify-between pt-4" style={{ borderTop: '1px solid var(--border)' }}>
        <div className="flex items-center gap-3">
          <button
            onClick={handleSave}
            disabled={loading || saveStatus === 'saving'}
            className="btn-secondary px-5 py-2 text-sm rounded-lg"
          >
            {saveStatus === 'saving' ? 'Saving…' : saveStatus === 'saved' ? '✓ Saved' : 'Save'}
          </button>
          {/* Phase 22.1: autosave status indicator (subtle). */}
          {readyToAutosave && (
            <span className="text-xs opacity-70">
              {autosave.status === 'saving' && <span className="text-slate-400">Autosaving…</span>}
              {autosave.status === 'saved' && <span className="text-emerald-400">Autosaved</span>}
              {autosave.status === 'error' && (
                <span className="text-red-300">
                  Autosave failed:{' '}
                  <button onClick={autosave.retry} className="underline">
                    retry
                  </button>
                </span>
              )}
            </span>
          )}
        </div>
        <button
          data-tour="compute-button"
          onClick={handleRunAnalysis}
          disabled={analyzing || currentScenario.vehicles.length === 0}
          className="btn-primary inline-flex items-center gap-2 text-sm px-5 py-2 rounded-lg"
        >
          {analyzing ? (
            <>
              <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
              </svg>
              Analyzing…
            </>
          ) : (
            <>
              Run Analysis
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
              </svg>
            </>
          )}
        </button>
      </div>

      {/* Vehicle Editor Modal */}
      {editingVehicle && (
        <VehicleEditor
          vehicle={editingVehicle}
          onSave={(updated: VehicleInput) => updateVehicle(updated.vehicle_id, updated)}
          onClose={() => setEditingVehicleId(null)}
          horizonMonths={horizonMonths}
          onEpaAutoFill={() => {
            const sc = currentScenarioRef.current;
            if (sc.state_code) applyGeographicDefaults(sc.state_code);
          }}
          geoGasPriceLabel={geoGasPriceLabel}
        />
      )}
    </div>
  );
}
